import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import ExternalServices from '../components/ExternalServices';
import ListaSpesa from '../components/ListaSpesa';
import CosaVorrei from '../components/CosaVorrei';
import SchedaServizio, { Parte, usatoDa } from '../components/SchedaServizio';
import { messaggioErrore } from '../utils/apiError';

// I servizi in un posto solo (29/09/2026: «alle API darei un menu apposito»;
// 30/09/2026: «Servizi», con dentro «Cosa puoi fare»). Pronti o collegati da
// te, ogni servizio è una scheda uguale: cos'è, com'è messo, chi lo usa.
const field = 'w-full border rounded-lg px-3 py-2 bg-surface text-sm';

// Come si usa ogni capacità integrata: i modelli pronti da aprire in Automazioni.
const USI = {
  'weather.forecast': [
    { modello: 'meteo', etichetta: 'Avviso del mattino se piove' },
    { modello: 'meteo-comando', etichetta: 'Comando @meteo' },
  ],
};

function Integrata({ azione, schema, uso }) {
  const navigate = useNavigate();
  const [testo, setTesto] = useState('');
  const [esito, setEsito] = useState('');
  const [errore, setErrore] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const primo = schema.ingressi.find(i => i.tipo === 'testo');
  const prova = async () => {
    setEsito(''); setErrore(''); setInCorso(true);
    try {
      const { data } = await client.post('/v1/capacita/prova/', { azione, inputs: { [primo.id]: testo } }, { timeout: 30000 });
      setEsito(data.risultati.text);
    } catch (e) { setErrore(messaggioErrore(e, 'Prova non riuscita.')); }
    finally { setInCorso(false); }
  };
  return <SchedaServizio nome={schema.etichetta} frase={`${schema.descrizione} Non va configurato e non serve una chiave.`}
    stato={{ testo: 'pronto', tono: 'ok' }} uso={uso}>
    <Parte titolo="Cosa puoi fare">
      <p className="font-medium text-ink">Mostra le previsioni del tempo</p>
      {primo && <form className="flex gap-2" onSubmit={e => { e.preventDefault(); prova(); }}>
        <input className={field} aria-label={`Prova ${schema.etichetta}`} placeholder={`Es. ${primo.esempio} domani`}
          value={testo} onChange={e => setTesto(e.target.value)} />
        <button className="border rounded px-4 py-2 shrink-0" disabled={inCorso || !testo.trim()}>{inCorso ? 'Chiedo…' : 'Prova'}</button>
      </form>}
      {errore && <p role="alert" className="text-sm text-warning">{errore}</p>}
      {esito && <p role="status" className="text-sm rounded bg-surface-2 px-3 py-2 whitespace-pre-wrap">{esito}</p>}
      <div className="flex flex-wrap gap-2">
        {(USI[azione] || []).map(u => <button key={u.modello} type="button" className="min-h-[36px] text-sm rounded-full border px-3 py-1 text-brand-700"
          onClick={() => navigate('/automations', { state: { modello: u.modello } })}>+ {u.etichetta}</button>)}
      </div>
    </Parte>
  </SchedaServizio>;
}

export default function ApiPage() {
  const [capacita, setCapacita] = useState({});
  const [flows, setFlows] = useState([]);
  const [errore, setErrore] = useState('');
  // «Cerca un servizio adatto»: apre «Collega un servizio» con la descrizione già scritta.
  const [daCercare, setDaCercare] = useState(null);
  useEffect(() => {
    client.get('/v1/capacita/').then(({ data }) => setCapacita(data.capacita || {}))
      .catch(e => setErrore(messaggioErrore(e)));
    // Le regole servono a dire chi usa cosa: senza, non si sapeva se cancellare
    // un'operazione avrebbe rotto un comando.
    client.get('/v1/workflows/').then(({ data }) => setFlows(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  // Le «pronte» sono solo quelle integrate: le operazioni che hai descritto
  // stanno già nella loro scheda «tua» (prima comparivano due volte).
  // Le capacità della casa stanno nella scheda di Home Assistant, con la lista della spesa.
  const integrate = Object.entries(capacita).filter(([azione, schema]) => schema.effetti === 'lettura'
    && !azione.startsWith('servizio:') && !azione.startsWith('home.'));
  const perAzione = azione => usatoDa(flows, step => step.action === azione);
  const perServizio = id => usatoDa(flows, step => step.action === 'http.call' && step.inputs?.service === id);
  return <div className="max-w-4xl mx-auto space-y-6">
    <header>
      <h1 className="text-2xl font-semibold">Servizi</h1>
      <p className="text-sm text-muted mt-1">Quello che le automazioni possono fare per te: i servizi pronti e quelli che
        colleghi tu. Tocca un servizio per vedere cosa puoi fare, provarlo e crearci un comando.</p>
    </header>
    {errore && <p role="alert" className="p-3 border rounded-lg bg-warning-bg text-warning">{errore}</p>}
    <CosaVorrei onCercaServizio={testo => setDaCercare({ testo, quando: Date.now() })} />
    {/* Un elenco solo (29/09/2026: «nella mia testa sono la stessa cosa»). */}
    <ExternalServices usoDi={perServizio} daCercare={daCercare}>
      {integrate.map(([azione, schema]) => <Integrata key={azione} azione={azione} schema={schema} uso={perAzione(azione)} />)}
      {capacita['shopping.list'] && <ListaSpesa schema={capacita['shopping.list']} schemaCasa={capacita['home.status'] || null}
        comandi={capacita['home.control'] || null}
        uso={[...perAzione('shopping.list'), ...perAzione('home.status'), ...perAzione('home.control')]} />}
    </ExternalServices>
  </div>;
}
